/**
 * ConnectWise Hosted API Pod embed page.
 *
 * Renders the Theo AI assistant in a minimal layout for ConnectWise Manage's
 * "Hosted API" pod. No sidebar, topbar, or app chrome—only the assistant UI.
 *
 * URL params:
 *   - cw_id: ConnectWise service ticket ID (ConnectWise substitutes [cw_id] automatically)
 *
 * Configure in ConnectWise: Setup Tables > Manage Hosted API Setup
 * URL: https://your-horizon-domain.com/embed/connectwise-pod?cw_id=[cw_id]
 * Origin: https://your-horizon-domain.com
 */
import React, { useEffect } from "react";
import { Box, Typography, Button, Paper, IconButton } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useAppSelector, useAppDispatch } from "../hooks";
import NeoAssistant from "../components/NeoAssistant";
import authAction from "../actions/authAction";
import config from "../config.js";
import { ActionTypes } from "../store/actionTypes";
import SupervisorApi from "../apis/supervisorApi";
import LogoutIcon from "../assets/img/logout.svg";
import {
  removeWebAuthTokenMirror,
  setWebAuthTokenMirror,
} from "../utils/authTokenStorage";
import { buildTicketAiOverviewContext } from "../utils/ticketAiOverviewContext";
import { NEUTRAL } from "../styles/brandTokens";

const POD_MANUAL_LOGIN_KEY = "connectwise_pod_manual_login_required";

const ConnectWisePodEmbed: React.FC = () => {
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const supervisorApi = React.useMemo(() => new SupervisorApi(), []);
  const { loginWithPopup, logout, isAuthenticated, isLoading, user, getAccessTokenSilently } = useAuth0();
  const [isLoggingIn, setIsLoggingIn] = React.useState(false);
  const [isSyncingSession, setIsSyncingSession] = React.useState(false);
  const [aiOverviewTicketContext, setAiOverviewTicketContext] = React.useState<string>("");
  const [ticketPayloadForLifecycle, setTicketPayloadForLifecycle] = React.useState<
    Record<string, unknown> | null
  >(null);
  const [ticketCustomerId, setTicketCustomerId] = React.useState<string | null>(null);
  const [loginError, setLoginError] = React.useState<string | null>(null);
  const [manualLoginRequired, setManualLoginRequired] = React.useState<boolean>(() => {
    try {
      return sessionStorage.getItem(POD_MANUAL_LOGIN_KEY) === "1";
    } catch {
      return false;
    }
  });
  
  const ticketId = searchParams.get("cw_id");
  const ticketDisplayId = ticketId || "";
  const currentUser = useAppSelector(
    (state: { auth?: { user?: { email?: string }; accessToken?: string | null } }) => state.auth?.user
  );
  const accessToken = useAppSelector(
    (state: { auth?: { accessToken?: string | null } }) => state.auth?.accessToken ?? null
  );
  const currentUserEmail = currentUser?.email ?? "";
  const auth0UserEmail = user?.email ?? "";
  const hasUserMismatch = Boolean(
    currentUserEmail && auth0UserEmail && currentUserEmail !== auth0UserEmail
  );
  const sessionReady = Boolean(isAuthenticated && accessToken && currentUserEmail && !hasUserMismatch);
  const activeUser = sessionReady ? currentUser : null;
  const userId = sessionReady ? currentUserEmail : "";

  const resetPodSessionState = React.useCallback(() => {
    removeWebAuthTokenMirror();
    dispatch({ type: ActionTypes.CLEAR_CHAT });
    dispatch(authAction.setAccessToken(null));
    dispatch(authAction.setUserData(null));
    setAiOverviewTicketContext("");
    setTicketPayloadForLifecycle(null);
    setTicketCustomerId(null);
  }, [dispatch]);

  const setManualLoginLock = React.useCallback((locked: boolean) => {
    setManualLoginRequired(locked);
    try {
      if (locked) {
        sessionStorage.setItem(POD_MANUAL_LOGIN_KEY, "1");
      } else {
        sessionStorage.removeItem(POD_MANUAL_LOGIN_KEY);
      }
    } catch {
      // Ignore sessionStorage failures in embedded browsers.
    }
  }, []);

  const completeAuthSession = React.useCallback(async () => {
    if (!user) {
      setIsLoggingIn(false);
      setIsSyncingSession(false);
      return;
    }

    const token = await getAccessTokenSilently({
      authorizationParams: {
        audience: config.audience || '',
      },
    });

    setWebAuthTokenMirror(token);
    dispatch(authAction.setAccessToken(token));
    dispatch(authAction.setUserData(user));
    setLoginError(null);
    setIsLoggingIn(false);
    setIsSyncingSession(false);
  }, [dispatch, getAccessTokenSilently, user]);

  useEffect(() => {
    let cancelled = false;

    const loadAiOverviewContext = async () => {
      if (!sessionReady || !ticketId || !userId) {
        setAiOverviewTicketContext("");
        setTicketPayloadForLifecycle(null);
        setTicketCustomerId(null);
        return;
      }

      try {
        const publish = (ticketPayload: any) => {
        if (cancelled) return;

        setAiOverviewTicketContext(buildTicketAiOverviewContext(ticketPayload));
        const t = ticketPayload?.ticket;
        setTicketPayloadForLifecycle(
          t && typeof t === "object" ? (t as Record<string, unknown>) : null
        );
        const nextCustomerId = ticketPayload?.ticket?.customer_id;
        setTicketCustomerId(
          nextCustomerId === undefined || nextCustomerId === null
            ? null
            : String(nextCustomerId)
        );
        };
        const ticketPayload = await supervisorApi.getTicketById(ticketId, userId, undefined, publish);
        publish(ticketPayload);
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to load ticket details for AI overview:", error);
        setAiOverviewTicketContext("");
        setTicketPayloadForLifecycle(null);
        setTicketCustomerId(null);
      }
    };

    loadAiOverviewContext();

    return () => {
      cancelled = true;
    };
  }, [sessionReady, supervisorApi, ticketId, userId]);

  useEffect(() => {
    const handleAuth = async () => {
      if (!isAuthenticated) {
        if (currentUserEmail) {
          resetPodSessionState();
        }
        setIsSyncingSession(false);
        setIsLoggingIn(false);
        return;
      }

      if (manualLoginRequired) {
        // Keep the pod on its local sign-in screen even if Auth0 silently restores
        // an SSO session in the background. User must click "Sign In" explicitly.
        setIsSyncingSession(false);
        setIsLoggingIn(false);
        return;
      }

      const needsAuthSync =
        Boolean(user?.email) &&
        (!currentUserEmail || !accessToken || hasUserMismatch);

      if (needsAuthSync) {
        try {
          setIsSyncingSession(true);
          if (hasUserMismatch) {
            resetPodSessionState();
          }

          await completeAuthSession();
        } catch (error: any) {
          console.error("Error getting access token:", error);
          setLoginError(error?.message || "Failed to finish sign-in. Please try again.");
          setIsLoggingIn(false);
          setIsSyncingSession(false);
        }
      }
    };

    if (!isLoading) {
      handleAuth();
    }
  }, [
    completeAuthSession,
    isAuthenticated,
    isLoading,
    accessToken,
    currentUserEmail,
    hasUserMismatch,
    manualLoginRequired,
    resetPodSessionState,
    user,
  ]);

  const handleLogin = async () => {
    if (isLoggingIn) return;
    
    setIsLoggingIn(true);
    setLoginError(null);
    try {
      setManualLoginLock(false);

      if (isAuthenticated && user?.email) {
        await completeAuthSession();
        return;
      }

      await loginWithPopup({
        authorizationParams: {
          prompt: 'login',
          audience: config.audience || '',
          scope: "openid profile email offline_access",
        },
      });
    } catch (error: any) {
      console.error("Login error:", error);
      setIsLoggingIn(false);
      setManualLoginLock(true);
      
      if (error?.error === "popup_closed" || error?.error === "cancelled") {
        setLoginError("Login was cancelled. Please try again.");
      } else if (error?.error === "invalid_request" || error?.message?.includes("couldn't find your session")) {
        setLoginError("Authentication session error. Please ensure cookies are allowed and try again.");
      } else if (error?.message?.includes("popup") || error?.message?.includes("blocked")) {
        setLoginError("Popup was blocked. Please allow popups for this site and try again.");
      } else {
        setLoginError(error?.message || error?.error_description || "Login failed. Please try again.");
      }
    }
  };

  const handleLogout = () => {
    resetPodSessionState();
    setManualLoginLock(true);
    setLoginError(null);
    setIsLoggingIn(false);
    setIsSyncingSession(false);
    // Pod logout should return to the local embedded sign-in screen without a full-page Auth0 redirect.
    // This avoids Auth0 "invalid returnTo" errors when the pod URL isn't in Allowed Logout URLs.
    logout({
      openUrl: false,
    });
  };

  if (isLoading || isLoggingIn || isSyncingSession) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          background: NEUTRAL.page,
        }}
      >
        <Typography sx={{ fontFamily: "var(--theo-font-sans)", color: "#5A5E73" }}>
          Loading...
        </Typography>
      </Box>
    );
  }

  if (manualLoginRequired || !sessionReady) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          background: NEUTRAL.page,
          p: 3,
        }}
      >
        <Box
          sx={{
            background: "#fff",
            borderRadius: 2,
            p: 4,
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
            maxWidth: 400,
            width: "100%",
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              fontFamily: "var(--theo-font-sans)",
              fontSize: "18px",
              fontWeight: 600,
              color: "#19213D",
              mb: 2,
            }}
          >
            Sign in to Theo
          </Typography>
          <Typography
            sx={{
              fontFamily: "var(--theo-font-sans)",
              fontSize: "14px",
              color: "#5A5E73",
              mb: 3,
            }}
          >
            Sign in to use Theo AI assistant.
          </Typography>
          {loginError && (
            <Typography
              sx={{
                fontFamily: "var(--theo-font-sans)",
                fontSize: "12px",
                color: "#d32f2f",
                mb: 2,
                textAlign: "center",
              }}
            >
              {loginError}
            </Typography>
          )}
          <Button
            variant="contained"
            onClick={handleLogin}
            disabled={isLoggingIn}
            sx={{
              backgroundColor: "#705D48",
              color: "#fff",
              fontFamily: "var(--theo-font-sans)",
              fontWeight: 600,
              px: 4,
              py: 1.5,
              borderRadius: 2,
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#5a4b3a",
              },
              "&:disabled": {
                backgroundColor: "#9a8a7a",
              },
            }}
          >
            {isLoggingIn ? "Signing in..." : "Sign In"}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        height: "100vh",
        width: "100%",
        minHeight: 0,
        background: "#fff",
        overscrollBehavior: "contain",
        overflow: "hidden",
        touchAction: "pan-y",
        pt: 2,
      }}
      onWheel={(e) => {
        e.stopPropagation();
      }}
      onTouchMove={(e) => {
        e.stopPropagation();
      }}
    >
      <Paper
        elevation={0}
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "calc(100% - 16px)",
          maxWidth: { xs: "100%", sm: "80%" },
          width: "100%",
          borderRadius: { xs: 0, sm: 2 },
          overflow: "hidden",
          background: "#fff",
          boxShadow: "0 0 12px rgba(155, 122, 239, 0.28), 0 0 24px rgba(155, 122, 239, 0.12), 0 1px 3px rgba(0, 0, 0, 0.12)",
        }}
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
            <NeoAssistant
              ticketId={ticketId}
              ticketDisplayId={ticketDisplayId}
              userId={userId}
              currentUser={activeUser}
              customerId={ticketCustomerId}
              isEmbedded={true}
              aiOverviewTicketContext={aiOverviewTicketContext}
              ticketPayloadForLifecycle={ticketPayloadForLifecycle}
              headerRightAction={
                <IconButton
                  size="small"
                  sx={{
                    backgroundColor: "#f3f4f6",
                    borderRadius: "50%",
                    width: 24,
                    height: 24,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 0,
                    mr: 0.5,
                    border: "1px solid #e5e7eb",
                    "&:hover": {
                      backgroundColor: "#e5e7eb",
                      borderColor: "#d1d5db",
                    },
                  }}
                  onClick={handleLogout}
                  aria-label="Logout"
                  title="Logout"
                >
                  <img
                    src={LogoutIcon}
                    alt="Logout"
                    style={{ width: 14, height: 14 }}
                  />
                </IconButton>
              }
            />
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default ConnectWisePodEmbed;
